import { InlineCalendar } from "PFComponents/calendar/inline_calendar";
import { InlineRangeCalendar } from "PFComponents/calendar/inline_calendar";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { ComponentProps, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./position_period_picker.module.scss";

type PositionPeriodPickerOwnProps = {
  currentPosition?: boolean;
  letCurrentPosition?: boolean;
  letDuration?: boolean;
  showLabel?: boolean;
  startDate: string | null;
  endDate: string | null;
  duration?: number | string | null;
  maxDate?: string | null;
  error?: ComponentProps<typeof InputFieldSet>["error"];
  handleChange: (
    startDate: string | null,
    endDate: string | null,
    duration: number | null,
    showDuration: boolean
  ) => void;
  handleToggleClick: (value: boolean) => void;
  locked?: boolean;
  disabledToogles?: boolean;
};

type PositionPeriodPickerProps = PositionPeriodPickerOwnProps &
  Omit<
    Partial<ComponentProps<typeof InlineRangeCalendar>>,
    keyof PositionPeriodPickerOwnProps | "handleChange"
  >;

const PositionPeriodPicker = (props: PositionPeriodPickerProps) => {
  const {
    currentPosition,
    letCurrentPosition = true,
    letDuration,
    showLabel = true,
    startDate,
    endDate,
    duration,
    maxDate,
    error,
    handleChange,
    handleToggleClick: handleCurrentToggle,
    locked,
    disabledToogles,
    ...calendarProps
  } = props;
  const { t } = useTranslation("profiles", { keyPrefix: "edit.fields" });

  const durationValue = duration as number | null;

  const [showDuration, setShowDuration] = useState<boolean>(
    !!(!currentPosition && letDuration && !!duration && !startDate)
  );

  useEffect(() => {
    // Clear dates or duration depending on initial showDuration state
    handleChange(startDate, endDate, durationValue, showDuration);
  }, []);

  const { formatISODate } = useDateFormatter();

  const calendarLabel = showLabel
    ? currentPosition
      ? t("periodPicker.startDate")
      : t("periodPicker.period")
    : "";
  const hasRange = startDate && endDate;
  const rangeStartDate = (hasRange ? formatISODate(startDate) : null) as string;
  const rangeEndDate = (hasRange ? formatISODate(endDate) : null) as string;

  return (
    <div className={css.root}>
      <div data-qa-id="period-picker" className={css.periodPicker}>
        {showDuration ? (
          <InputFieldSet
            label={t("periodPicker.duration")}
            tip={t("periodPicker.numberOfMonths")}
            value={duration as number | string | undefined}
            inputType="number"
            error={error}
            onChange={(val) => handleChange(startDate, endDate, val ? parseInt(val) : null, showDuration)}
            locked={locked}
            min={0}
          />
        ) : currentPosition && letCurrentPosition ? (
          <InlineCalendar
            {...calendarProps}
            label={calendarLabel}
            maxDate={formatISODate(maxDate)}
            handleChange={(start) => handleChange(start, null, durationValue, showDuration)}
            selectedDate={currentPosition && startDate ? formatISODate(startDate) : null}
            error={error}
            locked={locked}
          />
        ) : (
          <InlineRangeCalendar
            {...calendarProps}
            label={calendarLabel}
            maxDate={formatISODate(maxDate)}
            handleChange={(start, end) => handleChange(start, end, durationValue, showDuration)}
            startDate={rangeStartDate}
            endDate={rangeEndDate}
            error={error}
            locked={locked}
          />
        )}
      </div>
      {letDuration && (
        <Toggle
          label={t("periodPicker.durationQuestion")}
          qaId="period-picker-duration"
          checked={showDuration}
          disabled={disabledToogles}
          onChange={(value) => {
            if (value) {
              handleCurrentToggle(false);
            }
            handleChange(startDate, endDate, durationValue, value); // clear start/end dates
            setShowDuration(value);
          }}
        />
      )}
      {letCurrentPosition && (
        <Toggle
          label={t("periodPicker.currentQuestion")}
          qaId="period-picker-current"
          checked={currentPosition}
          disabled={disabledToogles}
          onChange={(value) => {
            if (value) {
              setShowDuration(false);
              handleChange(startDate, endDate, durationValue, showDuration); // clear duration
            }
            handleCurrentToggle(value);
          }}
        />
      )}
    </div>
  );
};

export default PositionPeriodPicker;
